import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import get from 'lodash/get';

import { NOON_TIME } from 'Shared/constants/constants';
import { updateItem } from 'Shared/actions/todos';
import { getMeetingItemByUuid } from 'Shared/selectors/common';
import { useTranslation } from 'Shared/utils/hooks/use-translation';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';
import WpIcon from 'Wpds/components/icon/WpIcon';
import DueByDropdown from 'Shared/components/DueByDropdown/DueByDropdown';

import 'react-dates/lib/css/_datepicker.css';
import './DueDateDropdown.scss';

type Props = {
  readonly uuid?: string;
  readonly onSelectedDueDate: Function;
  readonly dueByValue: number;
  readonly disabled?: boolean;
};

function formatDateForTitle(dueBy) {
  return moment.unix(dueBy).format('dddd, MMM DD, YYYY');
}

function DueDateDropdown({ uuid, onSelectedDueDate = null, dueByValue = null, disabled = false }: Props) {
  const { tr } = useTranslation();
  const dispatch = useDispatch();
  const dueBy = useSelector(state => dueByValue || get(getMeetingItemByUuid(uuid)(state), 'dueBy', null));
  const itemType = useSelector(state => dueByValue || get(getMeetingItemByUuid(uuid)(state), 'itemType', null));

  const initialTimeOfDay = dueBy ? moment.unix(dueBy).format('HH:mm') : NOON_TIME;

  const [timeOfDay, setTimeOfDay] = useState(initialTimeOfDay);

  useEffect(() => {
    setTimeOfDay(initialTimeOfDay);
  }, [dueBy, initialTimeOfDay]);

  const updateDateTimeDispatch = useCallback(
    async unixDateTime => {
      if (onSelectedDueDate) return onSelectedDueDate(unixDateTime);
      return dispatch(updateItem(uuid, { dueBy: unixDateTime, itemType }));
    },
    [dispatch, itemType, onSelectedDueDate, uuid]
  );

  const handleDateChanged = useCallback(
    unixDateTime => {
      updateDateTimeDispatch(unixDateTime);
    },
    [timeOfDay, updateDateTimeDispatch]
  );

  return (
    <DueByDropdown dueByDate={dueBy} handleUpdateDueByDate={handleDateChanged} disabled={disabled}>
      {dueBy ? (
        <WpTooltip>
          <WpTooltipTrigger>
            <div className="due-date-dropdown-modal-assigned-date w-100 d-flex pl-2">
              <span>{moment.unix(dueBy).format('MMM DD')}</span>
            </div>
          </WpTooltipTrigger>
          <WpTooltipContent>
            {tr('perform.due_date.dropdown.due_prefix')} {formatDateForTitle(dueBy)}
          </WpTooltipContent>
        </WpTooltip>
      ) : (
        <WpTooltip>
          <WpTooltipTrigger>
            <div className="due-date-dropdown-modal-unassigned-date w-100 d-flex pl-2">
              <span className="due-date-dropdown-modal-icon">
                <WpIcon name="calendar-plus" />
              </span>
              <div className="due-date-dropdown-unassigned-label d-inline-block d-sm-none text-super-muted">
                {tr('perform.due_date.dropdown.set_due_date')}
              </div>
            </div>
          </WpTooltipTrigger>
          <WpTooltipContent>{tr('perform.due_date.dropdown.add_due_date')}</WpTooltipContent>
        </WpTooltip>
      )}
    </DueByDropdown>
  );
}

export default React.memo(DueDateDropdown);
